/**
 * Sync Entity Schemas
 *
 * Types for platform data synchronization jobs and status tracking.
 */

import { z } from 'zod'

/**
 * Sync Window Schema
 *
 * When sync jobs are allowed to run. Kept in lock-step with the backend
 * runtime enum (`apps/backend/src/types/sync-enums.ts` `SYNC_WINDOW_VALUES`)
 * and with the hour/day rules in
 * `apps/backend/src/services/platform-data-sync/shared/window-hours.ts`
 * (`WINDOW_HOURS`), which is the authority for what each value means.
 *
 * SEMANTICS ARE NOT UNIFORM — read before writing UI copy. Most values label
 * a SKIP window: `BUSINESS_HOURS` means "blocked 06:00–22:00 school-local"
 * (i.e. runs overnight only), `OFF_HOURS` means "blocked 22:00–06:00",
 * `WEEKDAYS`/`WEEKENDS` mean "blocked on those days". `WORKING_HOURS` is the
 * exception: it INVERTS to "only run Mon–Fri 06:00–22:00", because a single
 * skip-block can't express "skip overnight AND weekends". `ANY_TIME` is never
 * blocked. Always render these with the disambiguating labels in
 * `SYNC_WINDOW_LABELS` — a bare "Business Hours" reads as the opposite of
 * what it does.
 */
export const SYNC_WINDOW_VALUES = [
  'ANY_TIME',
  'BUSINESS_HOURS',
  'OFF_HOURS',
  'WORKING_HOURS',
  'WEEKDAYS',
  'WEEKENDS'
] as const

export const SyncWindowSchema = z.enum(SYNC_WINDOW_VALUES)
export type SyncWindow = z.infer<typeof SyncWindowSchema>

/**
 * Operator-facing labels for each sync window.
 *
 * Single source of truth so every surface (admin dropdown, school config
 * table) says the same thing. Each label spells out the actual behaviour
 * because the skip-vs-only-run inversion is a genuine footgun.
 */
export const SYNC_WINDOW_LABELS: Record<SyncWindow, string> = {
  ANY_TIME: 'Any Time',
  BUSINESS_HOURS: 'Business Hours (skip 6am–10pm)',
  OFF_HOURS: 'Off Hours (skip 10pm–6am)',
  WORKING_HOURS: 'Working Hours (only Mon–Fri 6am–10pm)',
  WEEKDAYS: 'Weekdays (skip Mon–Fri)',
  WEEKENDS: 'Weekends (skip Sat–Sun)'
}

/**
 * Sync Priority Schema
 */
export const SyncPrioritySchema = z.enum(['HIGH', 'NORMAL', 'LOW', 'URGENT'])
export type SyncPriority = z.infer<typeof SyncPrioritySchema>

/**
 * Sync Job Status Schema
 *
 * The full set of `PlatformDataSyncJob.status` values, kept in lock-step with
 * the backend runtime enum (`apps/backend/src/types/sync-enums.ts`
 * `SYNC_JOB_STATUS_VALUES`). The terminal states are listed in
 * {@link TERMINAL_SYNC_JOB_STATUSES}; a poll loop must stop on ALL of them, so
 * the contract has to be able to represent them. ABANDONED (set by the reaper)
 * and SKIPPED_TOKEN_ABSENT (token-absent terminal non-failure) were previously
 * missing from this enum — a real drift bug, fixed here so the internal
 * job-status contract can carry every terminal value. SKIPPED_COVERED
 * (SCH-1671) is the USER_CONTENT_SYNC coverage-dedup no-op: non-failure,
 * no token claim, no SKY call. PARTIAL_SUCCESS is a terminal success with a
 * warning (some batches failed); the config rollup counts it as a success.
 */
export const SyncJobStatusSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'SUCCESS',
  'PARTIAL_SUCCESS',
  'FAILED',
  'CANCELLED',
  'ABANDONED',
  'SKIPPED_TOKEN_ABSENT',
  'SKIPPED_COVERED',
  'SKIPPED_NOT_BUILT'
])
export type SyncJobStatus = z.infer<typeof SyncJobStatusSchema>

/**
 * Child Jobs Summary Schema
 * Summary of child job statuses for parent sync jobs
 */
export const ChildJobsSummarySchema = z.object({
  totalChildJobs: z.number(),
  pendingJobs: z.number(),
  inProgressJobs: z.number(),
  successJobs: z.number(),
  failedJobs: z.number(),
  cancelledJobs: z.number(),
  overallStatus: z.string()
})

export type ChildJobsSummary = z.infer<typeof ChildJobsSummarySchema>

/**
 * Sync Job Stats Schema
 * Statistics from a completed sync job
 */
export const SyncJobStatsSchema = z.object({
  endTime: z.string(),
  startTime: z.string(),
  durationMs: z.number(),
  newRecords: z.number(),
  totalProcessed: z.number(),
  updatedRecords: z.number(),
  failedProcessed: z.number(),
  successfulProcessed: z.number()
})

export type SyncJobStats = z.infer<typeof SyncJobStatsSchema>

/**
 * Failed Item Schema
 * Details about an item that failed during sync
 */
export const FailedItemSchema = z.looseObject({
  name: z.string().optional(),
  error: z.string().optional(),
  timestamp: z.string().optional(),
  platformId: z.string().optional(),
  additionalInfo: z.record(z.string(), z.any()).optional()
})

export type FailedItem = z.infer<typeof FailedItemSchema>

/**
 * Sync Job Metadata Schema
 * Metadata attached to sync jobs
 */
export const SyncJobMetadataSchema = z.looseObject({
  isAsync: z.boolean(),
  triggeredBy: z.string().nullable(),
  manualTrigger: z.boolean(),
  executionMode: z.string().optional(),
  options: z
    .looseObject({
      platformUserIds: z.array(z.string()).optional(),
      forceRefresh: z.boolean().optional()
    })
    .optional(),
  stats: SyncJobStatsSchema.optional(),
  failedItems: z.array(FailedItemSchema).optional()
})

export type SyncJobMetadata = z.infer<typeof SyncJobMetadataSchema>

/**
 * Sync Job Schema
 * Complete sync job record
 */
export const SyncJobSchema = z.object({
  id: z.string(),
  publicId: z.string().optional(),
  schoolId: z.string(),
  schoolName: z.string().optional(),
  school: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string()
    })
    .optional(),
  syncType: z.string(),
  syncConfigId: z.string(),
  status: SyncJobStatusSchema,
  priority: z.string(),
  queuedAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  lastSyncedItemId: z.string().nullable(),
  lastSyncedItemDate: z.string().nullable(),
  totalItemsProcessed: z.number(),
  totalNewItems: z.number(),
  totalUpdatedItems: z.number(),
  totalFailedItems: z.number(),
  totalErrorItems: z.number(),
  nextPageToken: z.string().nullable(),
  childJobsSummary: ChildJobsSummarySchema.optional(),
  metadata: SyncJobMetadataSchema,
  createdAt: z.string(),
  updatedAt: z.string()
})

export type SyncJob = z.infer<typeof SyncJobSchema>

/**
 * Data Sync Status Schema
 * Configuration and status for a sync type
 */
/** One parent a child-only sync type runs as part of (SCH-3122). */
export const SyncParentRefSchema = z.object({
  syncType: z.string(),
  /** The parent's Sync Catalog label. */
  label: z.string()
})
export type SyncParentRef = z.infer<typeof SyncParentRefSchema>

export const DataSyncStatusSchema = z.object({
  id: z.string(),
  schoolId: z.string(),
  schoolName: z.string(),
  syncType: z.string(),
  syncGroup: z.string(),
  lastSyncJobId: z.string().nullable(),
  lastSuccessfulSync: z.string().nullable(),
  lastAttemptedSync: z.string().nullable(),
  lastSuccessfulDuration: z.number().nullable(),
  averageDuration: z.number().nullable(),
  syncFrequencyMinutes: z.number(),
  syncWindow: SyncWindowSchema,
  syncPriority: SyncPrioritySchema,
  nextScheduledSync: z.string().nullable(),
  lastScheduledSync: z.string().nullable(),
  forceRunAt: z.string().nullable(),
  customCronSchedule: z.string().nullable(),
  totalSuccessfulSyncs: z.number(),
  totalFailedSyncs: z.number(),
  consecutiveFailures: z.number(),
  isEnabled: z.boolean(),
  disabledReason: z.string().nullable(),
  disabledAt: z.string().nullable(),
  disabledBy: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastJob: SyncJobSchema.nullable(),
  lastSyncJob: SyncJobSchema.nullable(),
  /**
   * Set for a CHILD-ONLY type (SCH-3122): the parents that create its jobs with
   * the per-item metadata it needs, so it cannot be run on its own. Null for a
   * type that runs standalone. Read off the Sync Catalog (`SyncStep.childOf`).
   * Sent by `GET /v1/schools/:id/data-sync-configs`; other routes that return
   * config rows leave it out. The web does not read it: it bundles
   * `CHILD_SYNC_TYPE_PARENTS` (`./sync-child-types`), so a web build ahead of the
   * backend still knows the child-only types (SCH-3144).
   */
  childOf: z.array(SyncParentRefSchema).nullable().optional()
})

export type DataSyncStatus = z.infer<typeof DataSyncStatusSchema>
